import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  FileText, 
  User, 
  Calendar, 
  Clock, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Share2, 
  LogIn, 
  LogOut, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import LoadingSpinner from '../atoms/LoadingSpinner';
import toast from 'react-hot-toast';

interface ActivityLog {
  id: string;
  user_id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  status: string;
  ip_address: string;
  user_agent: string;
  details: any;
  created_at: string;
}

interface FilterOptions {
  entityType: string[];
  action: string[];
  status: string[];
  dateRange: {
    start: Date;
    end: Date;
  };
  searchTerm: string;
}

const ActivityLogPage: React.FC = () => {
  const { user } = useAuthStore();
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FilterOptions>({
    entityType: [],
    action: [],
    status: [],
    dateRange: {
      start: subDays(new Date(), 30),
      end: new Date()
    },
    searchTerm: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  // Fetch activity logs
  const fetchActivityLogs = async (reset = false) => {
    if (reset) {
      setPage(1);
      setActivityLogs([]);
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Build query with filters - only select from audit_logs table
      let query = supabase
        .from('audit_logs')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .range((reset ? 0 : (page - 1) * pageSize), (reset ? 0 : page * pageSize - 1) + pageSize - 1);
      
      // Apply filters
      if (filters.entityType.length > 0) {
        query = query.in('entity_type', filters.entityType);
      }
      
      if (filters.action.length > 0) {
        query = query.in('action', filters.action);
      }
      
      if (filters.status.length > 0) {
        query = query.in('status', filters.status);
      }
      
      if (filters.dateRange.start && filters.dateRange.end) {
        query = query
          .gte('created_at', filters.dateRange.start.toISOString())
          .lte('created_at', filters.dateRange.end.toISOString());
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      // Apply search filter client-side (for more complex searching)
      let filteredData = data || [];
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        filteredData = filteredData.filter(log => {
          return (
            log.entity_id.toLowerCase().includes(searchTerm) ||
            log.action.toLowerCase().includes(searchTerm) ||
            log.entity_type.toLowerCase().includes(searchTerm) ||
            (log.details && JSON.stringify(log.details).toLowerCase().includes(searchTerm))
          );
        });
      }
      
      if (reset) {
        setActivityLogs(filteredData);
      } else {
        setActivityLogs(prev => [...prev, ...filteredData]);
      }
      
      // Check if there are more logs to load
      setHasMore(filteredData.length === pageSize);
      
    } catch (error: any) {
      console.error('Failed to fetch activity logs:', error);
      setError(error.message || 'Failed to load activity logs');
      toast.error('Failed to load activity logs');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (user?.id) {
      fetchActivityLogs(true);
    }
  }, [user?.id]);

  // Fetch when filters change
  useEffect(() => {
    if (user?.id) {
      fetchActivityLogs(true);
    }
  }, [filters]);

  // Load more logs
  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage(prev => prev + 1);
      fetchActivityLogs();
    }
  };

  // Toggle log expansion
  const toggleExpand = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  // View log details
  const viewLogDetails = (log: ActivityLog) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Close details modal
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedLog(null);
  };

  // Update filters
  const updateFilter = (key: keyof FilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Toggle entity type filter
  const toggleEntityTypeFilter = (type: string) => {
    setFilters(prev => {
      const newEntityType = prev.entityType.includes(type)
        ? prev.entityType.filter(t => t !== type)
        : [...prev.entityType, type];
      
      return {
        ...prev,
        entityType: newEntityType
      };
    });
  };

  // Toggle action filter
  const toggleActionFilter = (action: string) => {
    setFilters(prev => {
      const newAction = prev.action.includes(action)
        ? prev.action.filter(a => a !== action)
        : [...prev.action, action];
      
      return {
        ...prev,
        action: newAction
      };
    });
  };

  // Toggle status filter
  const toggleStatusFilter = (status: string) => {
    setFilters(prev => {
      const newStatus = prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status];
      
      return {
        ...prev,
        status: newStatus
      };
    });
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      entityType: [],
      action: [],
      status: [],
      dateRange: {
        start: subDays(new Date(), 30),
        end: new Date()
      },
      searchTerm: ''
    });
  };

  // Get unique entity types, actions, and statuses for filter options
  const uniqueEntityTypes = Array.from(new Set(activityLogs.map(log => log.entity_type)));
  const uniqueActions = Array.from(new Set(activityLogs.map(log => log.action)));
  const uniqueStatuses = Array.from(new Set(activityLogs.map(log => log.status)));

  // Get icon for entity type
  const getEntityTypeIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'auth':
        return <User className="h-4 w-4 text-purple-500" />;
      case 'will':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'session':
        return <Activity className="h-4 w-4 text-orange-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get icon for action
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'view':
        return <Eye className="h-4 w-4 text-blue-500" />;
      case 'create':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'update':
        return <Edit className="h-4 w-4 text-orange-500" />;
      case 'delete':
        return <Trash2 className="h-4 w-4 text-red-500" />;
      case 'share':
        return <Share2 className="h-4 w-4 text-purple-500" />;
      case 'login':
        return <LogIn className="h-4 w-4 text-blue-500" />;
      case 'logout':
        return <LogOut className="h-4 w-4 text-gray-500" />;
      case 'download':
        return <Download className="h-4 w-4 text-green-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3" />
            Success
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertTriangle className="h-3 w-3" />
            Failed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      case 'denied':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <X className="h-3 w-3" />
            Denied
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch (error) {
      return dateString;
    }
  };

  // Export logs as CSV
  const exportLogs = () => {
    if (activityLogs.length === 0) {
      toast.error('No logs to export');
      return;
    }

    try {
      // Create CSV header
      const headers = ['Date', 'Entity Type', 'Entity ID', 'Action', 'Status', 'IP Address'];
      
      // Create CSV rows
      const rows = activityLogs.map(log => [
        formatDate(log.created_at),
        log.entity_type,
        log.entity_id,
        log.action,
        log.status,
        log.ip_address
      ]);
      
      // Combine header and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `activity-log-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Activity log exported successfully');
    } catch (error) {
      console.error('Failed to export logs:', error);
      toast.error('Failed to export logs');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Activity className="h-6 w-6 mr-2 text-primary-600" />
            Activity Log
          </h2>
          <p className="text-gray-600">
            Track all actions and events related to your account
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={Download}
            onClick={exportLogs}
            disabled={activityLogs.length === 0}
          >
            Export
          </Button>
          <Button
            variant="outline"
            icon={RefreshCw}
            onClick={() => fetchActivityLogs(true)}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search logs..."
              value={filters.searchTerm}
              onChange={(e) => updateFilter('searchTerm', e.target.value)}
              icon={Search}
            />
          </div>
          <Button
            variant={showFilters ? 'primary' : 'outline'}
            icon={Filter}
            onClick={() => setShowFilters(!showFilters)}
            className="sm:w-auto w-full"
          >
            Filters {filters.entityType.length > 0 || filters.action.length > 0 || filters.status.length > 0 ? 
              `(${filters.entityType.length + filters.action.length + filters.status.length})` : ''}
          </Button>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Filter Options</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Entity Type Filter */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Entity Type</h4>
                  <div className="space-y-2">
                    {uniqueEntityTypes.map(type => (
                      <label key={type} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.entityType.includes(type)}
                          onChange={() => toggleEntityTypeFilter(type)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 flex items-center gap-1">
                          {getEntityTypeIcon(type)}
                          <span className="capitalize">{type}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Action Filter */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Action</h4>
                  <div className="space-y-2">
                    {uniqueActions.map(action => (
                      <label key={action} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.action.includes(action)}
                          onChange={() => toggleActionFilter(action)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 flex items-center gap-1">
                          {getActionIcon(action)}
                          <span className="capitalize">{action}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Status Filter */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                  <div className="space-y-2">
                    {uniqueStatuses.map(status => (
                      <label key={status} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.status.includes(status)}
                          onChange={() => toggleStatusFilter(status)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          <span className="capitalize">{status}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Date Range Filter */}
                <div className="md:col-span-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Date Range</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={filters.dateRange.start.toISOString().split('T')[0]}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : subDays(new Date(), 30);
                          updateFilter('dateRange', {
                            ...filters.dateRange,
                            start: newDate
                          });
                        }}
                        className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">End Date</label>
                      <input
                        type="date"
                        value={filters.dateRange.end.toISOString().split('T')[0]}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : new Date();
                          updateFilter('dateRange', {
                            ...filters.dateRange,
                            end: newDate
                          });
                        }}
                        className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </div>
      
      {/* Activity Logs */}
      <div className="space-y-4">
        {isLoading && activityLogs.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" text="Loading activity logs..." />
          </div>
        ) : error ? (
          <Card className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Error Loading Activity Logs
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => fetchActivityLogs(true)} icon={RefreshCw}>
              Try Again
            </Button>
          </Card>
        ) : activityLogs.length === 0 ? (
          <Card className="p-6 text-center">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Activity Logs Found
            </h3>
            <p className="text-gray-600">
              {filters.entityType.length > 0 || filters.action.length > 0 || filters.status.length > 0 || filters.searchTerm
                ? 'Try adjusting your filters or search terms'
                : 'Your activity logs will appear here as you use the application'}
            </p>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {activityLogs.map((log) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
                          {getEntityTypeIcon(log.entity_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <h4 className="font-medium text-gray-900 capitalize">
                              {log.action} {log.entity_type}
                            </h4>
                            {getStatusBadge(log.status)}
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            ID: {log.entity_id}
                          </p>
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(log.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={expandedLogs.has(log.id) ? ChevronUp : ChevronDown}
                          onClick={() => toggleExpand(log.id)}
                          className="text-gray-500"
                        >
                          {expandedLogs.has(log.id) ? 'Less' : 'More'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewLogDetails(log)}
                        >
                          Details
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    {expandedLogs.has(log.id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 pt-4 border-t border-gray-100"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-xs font-medium text-gray-700 mb-1">IP Address</h5>
                            <p className="text-sm text-gray-900">{log.ip_address || 'Not available'}</p>
                          </div>
                          <div>
                            <h5 className="text-xs font-medium text-gray-700 mb-1">Browser</h5>
                            <p className="text-sm text-gray-900 truncate">{log.user_agent || 'Not available'}</p>
                          </div>
                        </div>
                        
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-xs font-medium text-gray-700 mb-1">Details</h5>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Log Details Modal */}
      {showDetails && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
                    {getEntityTypeIcon(selectedLog.entity_type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">
                      {selectedLog.action} {selectedLog.entity_type}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formatDate(selectedLog.created_at)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={X}
                  onClick={closeDetails}
                />
              </div>

              <div className="space-y-6">
                {/* Entity Information */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Entity Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Entity Type</p>
                      <p className="text-sm text-gray-900 capitalize">{selectedLog.entity_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Entity ID</p>
                      <p className="text-sm text-gray-900 font-mono">{selectedLog.entity_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Action</p>
                      <p className="text-sm text-gray-900 capitalize">{selectedLog.action}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <div>{getStatusBadge(selectedLog.status)}</div>
                    </div>
                  </div>
                </div>

                {/* Request Information */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Request Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">IP Address</p>
                      <p className="text-sm text-gray-900">{selectedLog.ip_address || 'Not available'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Timestamp</p>
                      <p className="text-sm text-gray-900">{formatDate(selectedLog.created_at)}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs text-gray-500">User Agent</p>
                    <p className="text-sm text-gray-900 break-all">{selectedLog.user_agent || 'Not available'}</p>
                  </div>
                </div>

                {/* Details */}
                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Additional Details</h4>
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  variant="primary"
                  onClick={closeDetails}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogPage;